/**
 * Google Tag Manager + GA4 DataLayer helpers.
 *
 * Every push is wrapped in try/catch so tracking can never break the app.
 * The GTM container (GTM-PKJQVVKG) and GA4 property (G-1JVEGP3SV3) are
 * configured externally — this module only pushes events to `window.dataLayer`.
 */

import { getClickIds } from "./clickIds";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

// Module-level user props — lean segmentation fields auto-merged into every push.
// Only non-PII identifiers go here (no email, no name).
let userProps: Record<string, unknown> | null = null;

export type UserType =
  | "internal"
  | "chargebee"
  | "corporate"
  | "direct"
  | "unknown";

/**
 * Set user identity and push a `user_identified` event.
 *
 * Can be called twice per session:
 *   Phase 1 (initKeycloak.ts) — token-only data, fires immediately at login.
 *   Phase 2 (useUserIdentification hook) — enriched with subscription data
 *           for chargebee users once the query settles.
 *
 * `userProps` stores lean segmentation fields (user_id, access_type,
 * user_type) that are auto-merged into every subsequent dataLayer push.
 * Subscription details are only included in the `user_identified` event
 * itself — they are not merged into other events.
 */
export function identifyUser(params: {
  user_id: string;
  access_type: string;
  user_type: UserType;
  subscription_status?: string;
  subscription_plan?: string;
  subscription_age_days?: number | null;
  trial_days_remaining?: number | null;
  squawk_channels: string;
  has_app_pro: boolean;
  has_equities_suite: boolean;
  /** SHA-256 hash of lowercased email for enhanced conversion matching. Never the raw email. */
  email_sha256?: string;
}) {
  // Only lean segmentation fields go into userProps (merged into every event)
  userProps = {
    user_id: params.user_id,
    access_type: params.access_type,
    user_type: params.user_type,
  };

  // The full payload (including subscription details) goes into the event only
  push({ event: "user_identified", ...params });
}

/**
 * Clear user identity. Called on logout.
 */
export function clearUser() {
  userProps = null;
}

function push(event: Record<string, unknown>) {
  try {
    window.dataLayer = window.dataLayer || [];
    // Click IDs are merged onto every push so sGTM can hand them off to
    // Meta CAPI / Google Ads / LinkedIn CAPI / Bing server-side without
    // each helper having to thread them through manually.
    window.dataLayer.push({
      ...event,
      ...getClickIds(),
      ...(userProps ?? {}),
    });
  } catch {
    // silently ignore — tracking must never break the app
  }
}

// ── Page view ────────────────────────────────────────────────────────
export function trackPageView(params: {
  page_path: string;
  page_title: string;
  // Attribution — included on the first page_view of a session
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer_domain?: string;
  landing_page?: string;
}) {
  push({ event: "page_view", ...params });
}

// ── Auth events ──────────────────────────────────────────────────────
export function trackLogin(params: {
  method: string;
  user_id?: string;
  // Attribution fields — forwarded from stored session attribution
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer_domain?: string;
  landing_page?: string;
}) {
  push({ event: "login", ...params });
}

export function trackLogout() {
  push({ event: "logout" });
}

// ── Signup intent (pricing modal opened / checkout CTA clicked) ──────
export function trackSignupIntent(params: {
  intent_type: "pricing_modal_opened" | "checkout_clicked" | "business_inquiry";
  plan_type?: string;
  plan_variant?: string;
  billing_cycle?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer_domain?: string;
  landing_page?: string;
  /** FirstPromoter referral token, so sGTM can relay affiliate conversions. */
  fp_ref?: string;
}) {
  push({ event: "signup_intent", ...params });
}

// ── Content view ─────────────────────────────────────────────────────
export function trackContentView(params: {
  content_id: string | number;
  content_title: string;
  content_type: string;
  content_category?: string;
  content_source?: string;
  content_tags?: string;
  content_tickers?: string;
  content_widget?: string;
}) {
  push({ event: "content_view", ...params });
}

// ── Content engagement (time spent reading) ─────────────────────────
export function trackContentEngagement(params: {
  content_id: string | number;
  content_title: string;
  content_type: string;
  content_widget?: string;
  engagement_time_ms: number;
}) {
  push({ event: "content_engagement", ...params });
}

// ── Search ───────────────────────────────────────────────────────────
export function trackSearchSubmit(params: {
  search_term: string;
  search_context: string;
  search_filters?: string;
}) {
  push({ event: "search", ...params });
}

export function trackSearchResults(params: {
  search_term: string;
  search_context: string;
  results_count: number;
  search_filters?: string;
}) {
  push({ event: "search_results", ...params });
}

// ── Filter usage ────────────────────────────────────────────────────
export function trackFilterApplied(params: {
  filter_context: string;
  filter_type: string;
  filter_value: string;
  filter_count: number;
}) {
  push({ event: "filter_applied", ...params });
}

// ── Feature usage ────────────────────────────────────────────────────
export function trackFeatureUsage(params: {
  feature_name: string;
  feature_action: string;
  feature_detail?: string;
}) {
  push({ event: "feature_usage", ...params });
}

